import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Checkbox, Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { CheckExpandable } from '@/components/CheckExpandable';
import { HealthCheckBlock } from '@/delivery-type-plugin/capabilities/health-check';
import { HEALTH_CHECK_FIELD } from '@/delivery-type-plugin/capabilities/health-check/fieldKeys';
import { InferenceFrameworkSetting } from '@/delivery-type-plugin/capabilities/maas-system-config/inference-framework-setting';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';

import type { EmbeddedNodeSpec } from '../nodeFields';
import { resolveEmbeddedHealthCheckEnabled } from '../nodeFields';
import styles from './group-spec-modal.module.less';

export interface GroupSpecDrawerEmbeddedSectionsProps {
  form: FormInstance;
  parentForm?: FormInstance;
  embeddedNodeConfig?: boolean;
  showInferenceFramework?: boolean;
  showTrainingImage?: boolean;
  customImageOnly?: boolean;
  isEdit?: boolean;
  presetArchitecture?: string;
  presetImageLocked?: boolean;
  lockActionsWhenFilled?: boolean;
}

/** 节点抽屉内嵌：推理框架 / 训练镜像（位于资源信息上方，无独立小节标题） */
export function GroupSpecDrawerEmbeddedSections({
  form,
  parentForm,
  embeddedNodeConfig,
  showInferenceFramework,
  showTrainingImage,
  customImageOnly,
  isEdit,
  presetArchitecture,
  presetImageLocked,
  lockActionsWhenFilled,
}: GroupSpecDrawerEmbeddedSectionsProps) {
  if (!embeddedNodeConfig) return null;
  if (!showInferenceFramework && !showTrainingImage) return null;

  return (
    <InferenceFrameworkSetting
      form={form}
      compact
      isEdit={isEdit}
      presetArchitecture={presetArchitecture}
      presetImageLocked={presetImageLocked}
      lockActionsWhenFilled={lockActionsWhenFilled}
      customImageOnly={customImageOnly}
      showPullAuth={Boolean(showInferenceFramework || showTrainingImage)}
      systemField="system"
      parentForm={parentForm}
      skipResetNodeGroups
      embeddedNodeConfig
    />
  );
}

export interface GroupSpecDrawerAdvancedConfigSectionProps {
  form: FormInstance;
  embeddedNodeConfig?: boolean;
  /** 展示探活；兼容 showHealthCheck 别名 */
  showAdvancedConfig?: boolean;
  showHealthCheck?: boolean;
}

/** 节点抽屉探活配置（与初始化命令 / 环境变量相同的 CheckExpandable 卡片交互） */
export function GroupSpecDrawerAdvancedConfigSection({
  form,
  embeddedNodeConfig,
  showAdvancedConfig,
  showHealthCheck,
}: GroupSpecDrawerAdvancedConfigSectionProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const advancedVisible = showAdvancedConfig ?? showHealthCheck;
  const healthCheckEnabledFlag = Form.useWatch('_healthCheckEnabled', form);
  const healthCheckConfig = Form.useWatch(HEALTH_CHECK_FIELD, form);
  const healthCheckEnabled = resolveEmbeddedHealthCheckEnabled({
    _healthCheckEnabled: healthCheckEnabledFlag,
    _containerDeployHealthCheck: healthCheckConfig as Record<string, unknown> | undefined,
  } as EmbeddedNodeSpec);
  const healthBlockMode = sectionReadOnly ? 'preview' : 'edit';

  useEffect(() => {
    if (sectionReadOnly) return;
    if (form.getFieldValue('_healthCheckEnabled') === false) return;
    if (!healthCheckEnabled || form.getFieldValue('_healthCheckEnabled') === true) return;
    form.setFieldValue('_healthCheckEnabled', true);
  }, [form, healthCheckConfig, healthCheckEnabled, sectionReadOnly]);

  if (!embeddedNodeConfig || !advancedVisible) return null;

  return (
    <>
      <Form.Item field="_healthCheckEnabled" hidden triggerPropName="checked">
        <Checkbox />
      </Form.Item>
      <Card className={styles.configCard} bordered={false}>
        <CheckExpandable
          label={t('data-center:probe')}
          checked={healthCheckEnabled}
          onChange={sectionReadOnly ? undefined : (checked) => form.setFieldValue('_healthCheckEnabled', checked)}
          contentStyle={{ paddingTop: 8 }}
        >
          <HealthCheckBlock
            form={form}
            paths={{ config: HEALTH_CHECK_FIELD }}
            blockMode={healthBlockMode}
            fieldModes={{}}
            actionModes={{}}
          />
        </CheckExpandable>
      </Card>
    </>
  );
}

/** @deprecated 使用 GroupSpecDrawerAdvancedConfigSection */
export const GroupSpecDrawerHealthCheckSection = GroupSpecDrawerAdvancedConfigSection;
